import {
  IsoDate,
  IsoDateTime,
  IsoDateTimeTz, IsoDuration,
  IsoTime,
  IsoTimeTz,
  IsoWeekDuration
} from '../../types/iso-date';
import { isIsoDate } from '../validation/is-iso-date.function';
import { isIsoDateTime } from '../validation/is-iso-date-time.function';
import { isIsoDateTimeTz } from '../validation/is-iso-date-time-tz.function';
import { isIsoTime } from '../validation/is-iso-time.function';
import { isIsoDuration } from '../validation/is-iso-duration.function';
import { isIsoTimeTz } from '../validation/is-iso-time-tz.function';

export interface DeconstructedDate {
  _type: 'date';
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

export interface DeconstructedTime {
  _type: 'time';
  hours: number; // 0-23
  minutes: number; // 0-59
  seconds: number; // 0-59
  milliseconds: number; // 0-999
}

export interface DeconstructedDateTime {
  _type: 'dateTime';
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hours: number; // 0-23
  minutes: number; // 0-59
  seconds: number; // 0-59
  milliseconds: number; // 0-999
}

export interface DeconstructedTimeOffset {
  offsetSign: '+' | '-';
  offsetHours: number; // 0-14
  offsetMinutes: number; // 0-59
}

export interface DeconstructedDateTimeTz {
  _type: 'dateTimeTz';
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hours: number; // 0-23
  minutes: number; // 0-59
  seconds: number; // 0-59
  milliseconds: number; // 0-999
  offset: 'Z' | DeconstructedTimeOffset;
}

export interface DeconstructedTimeTz {
  _type: 'timeTz';
  hours: number; // 0-23
  minutes: number; // 0-59
  seconds: number; // 0-59
  milliseconds: number; // 0-999
  offset: 'Z' | DeconstructedTimeOffset;
}

export interface DeconstructedDuration {
  _type: 'duration';
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
}


export interface DeconstructedWeekDuration {
  _type: 'weekDuration';
  years?: number;
  weeks?: number;
}


export function deconstructDate<T extends IsoDate | IsoDateTime | IsoDateTimeTz | IsoTime | IsoTimeTz | IsoDuration | IsoWeekDuration>(date: T) :
  T extends IsoDate ? DeconstructedDate :
  T extends IsoTime ? DeconstructedTime :
  T extends IsoDateTime ? DeconstructedDateTime :
  T extends IsoTimeTz ? DeconstructedTimeTz :
  T extends IsoDateTimeTz ? DeconstructedDateTimeTz :
  T extends IsoWeekDuration ? DeconstructedWeekDuration :
  T extends IsoDuration ? DeconstructedDuration : never {
  if(isIsoDate(date))
    return deconstructIsoDate(date) as any;
  if(isIsoDateTime(date))
    return deconstructIsoDateTime(date) as any;
  if(isIsoDateTimeTz(date))
    return deconstructIsoDateTimeTz(date) as any;
  if(isIsoTime(date))
    return deconstructIsoTime(date) as any;
  if(isIsoTimeTz(date))
    return deconstructIsoTimeTz(date) as any;
  if(isIsoDuration(date))
    return deconstructIsoDateDuration(date) as any;
  throw new Error('deconstructDate: invalid ISO date/time string');
}

function deconstructIsoDate(date: IsoDate): DeconstructedDate {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error('deconstructDate: invalid ISO date string');
  return {
    _type: 'date',
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3])
  };
}

function deconstructIsoDateTime(date: IsoDateTime): DeconstructedDateTime {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?$/.exec(date);
  if (!m) throw new Error('deconstructDate: invalid ISO date-time string');
  const deconstructed: DeconstructedDateTime = {
    _type: 'dateTime',
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hours: Number(m[4]),
    minutes: Number(m[5]),
    seconds: Number(m[6]),
    milliseconds: 0
  };
  if (m[7]) {
    const milliseconds = m[7].padEnd(3, '0').slice(0, 3); // Normalize to milliseconds
    deconstructed.milliseconds = Number(milliseconds);
  }
  return deconstructed;
}

function deconstructIsoDateTimeTz(date: IsoDateTimeTz): DeconstructedDateTimeTz {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/.exec(date);
  if (!m) throw new Error('deconstructDate: invalid ISO date-time-tz string');
  const deconstructed: DeconstructedDateTimeTz = {
    _type: 'dateTimeTz',
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hours: Number(m[4]),
    minutes: Number(m[5]),
    seconds: Number(m[6]),
    milliseconds: 0,
    offset: m[8] === 'Z' ? 'Z' : {
      offsetSign: m[8][0] as '+' | '-',
      offsetHours: Number(m[8].slice(1, 3)),
      offsetMinutes: Number(m[8].slice(4, 6))
    }
  }
  if (m[7]) {
    const milliseconds = m[7].padEnd(3, '0').slice(0, 3); // Normalize to milliseconds
    deconstructed.milliseconds = Number(milliseconds);
  }
  return deconstructed;
}

function deconstructIsoTime(time: IsoTime): DeconstructedTime {
  const m = /^(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?$/.exec(time);
  if (!m) throw new Error('deconstructDate: invalid ISO time string');
  const deconstructed: DeconstructedTime = {
    _type: 'time',
    hours: Number(m[1]),
    minutes: Number(m[2]),
    seconds: Number(m[3]),
    milliseconds: 0
  };
  if (m[4]) {
    const milliseconds = m[4].padEnd(3, '0').slice(0, 3); // Normalize to milliseconds
    deconstructed.milliseconds = Number(milliseconds);
  }
  return deconstructed;
}

function deconstructIsoTimeTz(time: IsoTimeTz): DeconstructedTimeTz {
  const m = /^(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})$/.exec(time);
  if (!m) throw new Error('deconstructDate: invalid ISO time-tz string');
  const deconstructed: DeconstructedTimeTz = {
    _type: 'timeTz',
    hours: Number(m[1]),
    minutes: Number(m[2]),
    seconds: Number(m[3]),
    milliseconds: 0,
    offset: m[5] === 'Z' ? 'Z' : {
      offsetSign: m[5][0] as '+' | '-',
      offsetHours: Number(m[5].slice(1, 3)),
      offsetMinutes: Number(m[5].slice(4, 6))
    }
  };
  if (m[4]) {
    const milliseconds = m[4].padEnd(3, '0').slice(0, 3); // Normalize to milliseconds
    deconstructed.milliseconds = Number(milliseconds);
  }
  return deconstructed;
}

function deconstructIsoWeekDuration(weekDuration: string): DeconstructedWeekDuration {
  const m = /^P(?:(\d+)Y)?(?:(\d+)W)?$/.exec(weekDuration);
  if (!m) throw new Error('deconstructDate: invalid ISO week duration string');
  const deconstructed: DeconstructedWeekDuration = {_type: 'weekDuration'};
  if (m[1]) deconstructed.years = Number(m[1]);
  if (m[2]) deconstructed.weeks = Number(m[2]);
  return deconstructed;
}

function deconstructIsoDateDuration(duration: string): DeconstructedDuration {
  const m = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)(?:\.(\d{1,6}))?S)?)?$/.exec(duration);
  if (!m) throw new Error('deconstructDate: invalid ISO duration string');
  const deconstructed: DeconstructedDuration = { _type: 'duration' } as DeconstructedDuration;
  deconstructed.years = m[1] ? Number(m[1]) : 0;
  deconstructed.months = m[2] ? Number(m[2]) : 0;
  deconstructed.days = m[3] ? Number(m[3]) : 0;
  deconstructed.hours = m[4] ? Number(m[4]) : 0;
  deconstructed.minutes = m[5] ? Number(m[5]) : 0;
  deconstructed.seconds = m[6] ? Number(m[6]) : 0;
  deconstructed.milliseconds = m[7] ? Number(m[7].padEnd(3, '0').slice(0, 3)) : 0;
  return deconstructed;
}
